import React from "react";
import { Box, Text } from "@chakra-ui/react";
import { Section, sectionSpacingY } from "components/section";
import { FillImage } from "components/images";
import { Bullets } from "components/bullets";
import { Diptych } from "ui/diptych";
import { Button } from "ui/button";
import firstImage from "../../../../public/assets/shared-illustrations/diptych-1.png";
import secondImage from "../../../../public/assets/shared-illustrations/diptych-2.png";
import thirdImage from "../../../../public/assets/shared-illustrations/diptych-3.png";

export function SecurityDiptychs() {
  return (
    <>
      <Section>
        <Diptych
          media={
            <FillImage
              src={firstImage}
              alt="Abstract illustration of data flowing from top-to-bottom between three rows. The first row is labeled Data Warehouse with logos for Snowflake, Databricks, and Google BigQuery. The second row is labeled cloud bucket. The third is labeled destinations with logos for Facebook, HubSpot, and TikTok."
            />
          }
          direction="right"
          chip={{ text: "Your data in your warehouse" }}
          title="Novel hybrid architecture"
          actions={
            <Button hasRightChevron href="/docs/security/overview#basic-architecture-overview">
              See our product architecture
            </Button>
          }
          descriptionAs="div"
          description={
            <Box fontSize="15px">
              <Text>
                Hightouch never stores any of your data. We believe you should maintain control and take advantage of
                your existing architecture. Here’s how your data passes through Hightouch:
              </Text>
              <Bullets
                spacing="smaller"
                mt="smaller"
                textAlign="left"
                items={[
                  "Hightouch runs queries directly on your warehouse.",
                  "You can choose to store all customer data temporarily in your cloud.",
                  "Hightouch transfers data to your destination without ever storing it.",
                  "Hightouch also supports [AWS PrivateLink](https://hightouch.com/docs/security/aws-privatelink) for Snowflake in certain regions.",
                ]}
              />
            </Box>
          }
        />
      </Section>

      <Section>
        <Diptych
          media={
            <FillImage
              src={secondImage}
              alt="Abstract illustration representing the secure relationship between enterprise teams like Data, Marketing, and Operations teams with Hightouch resources like Audiences, Models, and Syncs."
            />
          }
          direction="left"
          chip={{ text: "Control access on your own terms" }}
          title="Enterprise-ready on day one"
          actions={
            <Button hasRightChevron href="/blog/enterprise-ready-on-day-1">
              See our governance features
            </Button>
          }
          descriptionAs="div"
          description={
            <Box fontSize="15px">
              <Text>
                Hightouch provides the control and governance capabilities you need to safely plug Reverse ETL into your
                tech stack.
              </Text>
              <Bullets
                spacing="smaller"
                textAlign="left"
                mt="smaller"
                items={[
                  "**SSO:** Manage user authentication centrally with SAML SSO and keep access grants up-to-date with support for SCIM.",
                  "**Role-based access:** Leverage fine-grained permissions and custom roles to control who has access to specific workspace resources (models, destinations, and syncs).",
                  "**Row-level access:** Grant users access to data based on geography, business unit, or any other parameter.",
                  "**Row-level sync controls:** Control which data can be synced to each of your destinations, to ensure data only travels to approved tools.",
                  "**Audit logs:** Track, monitor, and search all in-app user activity. Export reports and alerts to your preferred tools.",
                  "**Approval flows:** Require users to receive in-app approval before pushing changes into production. Rollback unintended changes immediately through Git.",
                ]}
              />
            </Box>
          }
        />
      </Section>

      <Section my={0} pb={sectionSpacingY}>
        <Diptych
          media={
            <FillImage
              src={thirdImage}
              alt="Abstract illustration highlighting SOC 2 Type 2, AES-256, HIPAA, and GDPR security standards."
            />
          }
          direction="right"
          chip={{ text: "We follow industry security standards" }}
          title="Product security"
          actions={
            <Button hasRightChevron href="/docs/security/overview">
              Learn about data security
            </Button>
          }
          descriptionAs="div"
          description={
            <Box fontSize="15px">
              <Text>
                Our platform architecture and industry compliance certifications mean that your data is always secure.
              </Text>

              <Bullets
                spacing="smaller"
                mt="smaller"
                textAlign="left"
                items={[
                  "We complete regular security-design reviews, pen tests using trusted security vendors, and conduct regular audits such as SOC 2 Type 2.",
                  "We encrypt all data at rest and protect it in transit with TLS. Hightouch’s metadata database is encrypted by Amazon AWS using standard AES-256 encryption algorithms.",
                  "We limit internal access to tools and resources using time-based access.",
                ]}
              />
            </Box>
          }
        />
      </Section>
    </>
  );
}
